import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, RefreshCw, Clock, FileText, TrendingUp, TrendingDown,
  Activity, DollarSign, Package, Users, BarChart3,
  PieChart as PieChartIcon, X, Filter, Search, Calendar,
  GitBranch, Target, Layers, AlertTriangle, Info, Shield, CheckCircle,
  Globe
} from 'lucide-react';
import { format } from 'date-fns';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, Area, AreaChart, ComposedChart,
  Treemap as RechartsTreemap,
} from 'recharts';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO 3166-1 numeric -> alpha-3 mapping for world map matching
const ISO_NUMERIC_TO_ALPHA3 = {
  "004":"AFG","008":"ALB","012":"DZA","020":"AND","024":"AGO","028":"ATG","032":"ARG","036":"AUS",
  "040":"AUT","044":"BHS","048":"BHR","050":"BGD","051":"ARM","052":"BRB","056":"BEL","064":"BTN",
  "068":"BOL","070":"BIH","072":"BWA","076":"BRA","084":"BLZ","090":"SLB","096":"BRN","100":"BGR",
  "104":"MMR","108":"BDI","112":"BLR","116":"KHM","120":"CMR","124":"CAN","140":"CAF","144":"LKA",
  "148":"TCD","152":"CHL","156":"CHN","170":"COL","174":"COM","178":"COG","180":"COD","188":"CRI",
  "191":"HRV","192":"CUB","196":"CYP","203":"CZE","204":"BEN","208":"DNK","214":"DOM","218":"ECU",
  "222":"SLV","226":"GNQ","231":"ETH","232":"ERI","233":"EST","242":"FJI","246":"FIN","250":"FRA",
  "262":"DJI","266":"GAB","268":"GEO","270":"GMB","276":"DEU","288":"GHA","300":"GRC","320":"GTM",
  "324":"GIN","328":"GUY","332":"HTI","340":"HND","344":"HKG","348":"HUN","352":"ISL","356":"IND",
  "360":"IDN","364":"IRN","368":"IRQ","372":"IRL","376":"ISR","380":"ITA","384":"CIV","388":"JAM",
  "392":"JPN","398":"KAZ","400":"JOR","404":"KEN","408":"PRK","410":"KOR","414":"KWT","417":"KGZ",
  "418":"LAO","422":"LBN","426":"LSO","428":"LVA","430":"LBR","434":"LBY","440":"LTU","442":"LUX",
  "450":"MDG","454":"MWI","458":"MYS","462":"MDV","466":"MLI","470":"MLT","478":"MRT","480":"MUS",
  "484":"MEX","496":"MNG","498":"MDA","504":"MAR","508":"MOZ","512":"OMN","516":"NAM","524":"NPL",
  "528":"NLD","540":"NCL","554":"NZL","558":"NIC","562":"NER","566":"NGA","578":"NOR","586":"PAK",
  "591":"PAN","598":"PNG","600":"PRY","604":"PER","608":"PHL","616":"POL","620":"PRT","630":"PRI",
  "634":"QAT","642":"ROU","643":"RUS","646":"RWA","682":"SAU","686":"SEN","688":"SRB","694":"SLE",
  "702":"SGP","703":"SVK","704":"VNM","705":"SVN","706":"SOM","710":"ZAF","716":"ZWE","724":"ESP",
  "728":"SSD","729":"SDN","740":"SUR","748":"SWZ","752":"SWE","756":"CHE","760":"SYR","762":"TJK",
  "764":"THA","768":"TGO","780":"TTO","784":"ARE","788":"TUN","792":"TUR","800":"UGA","804":"UKR",
  "807":"MKD","818":"EGY","826":"GBR","834":"TZA","840":"USA","854":"BFA","858":"URY","860":"UZB",
  "862":"VEN","887":"YEM","894":"ZMB","-99":"KOS"
};

const API_URL = "";

const GRADIENT_COLORS = [
  ['#6366f1', '#8b5cf6'], ['#10b981', '#34d399'], ['#f59e0b', '#fbbf24'],
  ['#ef4444', '#f87171'], ['#06b6d4', '#22d3ee'], ['#ec4899', '#f472b6'],
  ['#f97316', '#fb923c'], ['#8b5cf6', '#a78bfa']
];
const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#f97316', '#8b5cf6'];

const HEALTH_COLORS = {
  good: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', gradient: ['#10b981', '#34d399'] },
  warning: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', gradient: ['#f59e0b', '#fbbf24'] },
  danger: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', gradient: ['#ef4444', '#f87171'] },
  normal: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500/30', gradient: null },
};

const formatValue = (value, formatType) => {
  if (value === null || value === undefined) return '-';
  if (formatType === 'currency') {
    if (Math.abs(value) >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
    if (Math.abs(value) >= 100000) return `${(value / 100000).toFixed(2)} L`;
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
  }
  if (formatType === 'percentage') return `${value.toFixed(1)}%`;
  if (typeof value === 'number') {
    if (Math.abs(value) >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
    if (Math.abs(value) >= 100000) return `${(value / 100000).toFixed(2)} L`;
    return value >= 1000 ? value.toLocaleString('en-IN') : value.toFixed(2);
  }
  return value;
};

const getKPIIcon = (title) => {
  const l = title.toLowerCase();
  if (l.includes('revenue') || l.includes('sales') || l.includes('value') || l.includes('amount')) return DollarSign;
  if (l.includes('qty') || l.includes('quantity') || l.includes('inventory')) return Package;
  if (l.includes('user') || l.includes('customer') || l.includes('supplier')) return Users;
  if (l.includes('profit') || l.includes('margin')) return TrendingUp;
  if (l.includes('ratio') || l.includes('rate')) return Activity;
  return BarChart3;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-xl p-4 shadow-2xl">
        <p className="text-slate-300 text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-white font-semibold" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString('en-IN') : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// ─── 5 VITAL SIGNS ───
const KPICard = ({ title, value, format: formatType, index, health, audit }) => {
  const Icon = getKPIIcon(title);
  const isNegative = typeof value === 'number' && value < 0;
  const hc = HEALTH_COLORS[health] || HEALTH_COLORS.normal;
  const gradient = hc.gradient || GRADIENT_COLORS[index % GRADIENT_COLORS.length];
  const [showAudit, setShowAudit] = useState(false);

  return (
    <div className="relative group" data-testid={`kpi-card-${index}`}>
      <div 
        className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-xl transform transition-all duration-300 hover:scale-105 hover:shadow-2xl ${health !== 'normal' ? `ring-2 ${hc.border}` : ''}`}
        style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}
      >
        <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-8 -translate-y-8">
          <div className="absolute inset-0 bg-white/10 rounded-full blur-2xl"></div>
        </div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl">
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex items-center gap-1.5">
              {health === 'good' && <CheckCircle className="w-4 h-4 text-white/80" />}
              {health === 'warning' && <AlertTriangle className="w-4 h-4 text-white/80" />}
              {health === 'danger' && <AlertTriangle className="w-4 h-4 text-white animate-pulse" />}
              {isNegative ? <TrendingDown className="w-4 h-4 text-white/70" /> : <TrendingUp className="w-4 h-4 text-white/70" />}
              {audit && (
                <button onClick={(e) => { e.stopPropagation(); setShowAudit(!showAudit); }} className="p-1 hover:bg-white/20 rounded-lg transition-colors" data-testid={`audit-btn-${index}`}>
                  <Info className="w-3.5 h-3.5 text-white/60" />
                </button>
              )}
            </div>
          </div>
          <p className="text-white/80 text-xs font-medium tracking-wide uppercase mb-1.5">{title}</p>
          <p className="text-2xl font-black tracking-tight">{formatValue(value, formatType)}</p>
          {health !== 'normal' && (
            <div className={`mt-2 flex items-center gap-1 text-xs font-medium ${health === 'good' ? 'text-white/80' : 'text-white/90'}`}>
              <Shield className="w-3 h-3" />
              {health === 'good' ? 'On Track' : health === 'warning' ? 'Needs Attention' : 'Critical'}
            </div>
          )}
        </div>
      </div>
      {/* Audit Trail Popover */}
      {showAudit && audit && (
        <div className="absolute z-20 top-full mt-2 left-0 right-0 bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-2xl text-xs" data-testid={`audit-panel-${index}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-indigo-400 font-semibold uppercase tracking-wider">Data Lineage</span>
            <button onClick={() => setShowAudit(false)} className="text-slate-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="space-y-1.5 text-slate-300">
            <p><span className="text-slate-500">Formula:</span> {audit.formula}</p>
            <p><span className="text-slate-500">Source:</span> {audit.source_file}</p>
            <p><span className="text-slate-500">Rows:</span> {audit.rows_used} of {audit.total_rows}</p>
            <p><span className="text-slate-500">Computed:</span> {audit.computed_at ? format(new Date(audit.computed_at), 'MMM d, HH:mm:ss') : '-'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── 1 ALERT SECTION ───
const AlertsSection = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="alerts-section">
      {alerts.map((alert, i) => (
        <div key={i} className={`flex items-center gap-3 px-5 py-3 rounded-xl border backdrop-blur-sm ${
          alert.type === 'danger' ? 'bg-red-500/10 border-red-500/30 text-red-300' : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
        }`} data-testid={`alert-${i}`}>
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${alert.type === 'danger' ? 'text-red-400' : 'text-amber-400'}`} />
          <span className="text-sm font-medium">{alert.message}</span>
          <span className={`ml-auto text-xs px-2 py-0.5 rounded-full font-semibold ${
            alert.type === 'danger' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
          }`}>{alert.type.toUpperCase()}</span>
        </div>
      ))}
    </div>
  );
};

// ─── DRILL-DOWN MODAL ───
const DrilldownModal = ({ open, onClose, dashboardId, column, value }) => {
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && dashboardId) {
      setLoading(true);
      const params = new URLSearchParams();
      if (column) params.set('column', column);
      if (value) params.set('value', value);
      axios.get(`${API_URL}/api/dashboards/${dashboardId}/drilldown?${params}`, { withCredentials: true })
        .then(res => { setData(res.data.data || []); setColumns(res.data.columns || []); setTotal(res.data.total || 0); })
        .catch(() => {}).finally(() => setLoading(false));
    }
  }, [open, dashboardId, column, value]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-[90vw] max-w-5xl max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()} data-testid="drilldown-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Search className="w-5 h-5 text-indigo-400" />Drill-down Details</h3>
            {column && value && <p className="text-sm text-slate-400 mt-1">Filtered by <span className="text-indigo-300 font-medium">{column}</span> = <span className="text-white font-medium">{value}</span> <span className="text-slate-500">({total} rows)</span></p>}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></Button>
        </div>
        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>
          ) : data.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800/90 backdrop-blur-sm">
                <tr>{columns.map(col => <th key={col} className="text-left py-3 px-3 text-xs font-semibold tracking-wider uppercase text-indigo-300 border-b border-slate-700">{col}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {data.slice(0, 100).map((row, i) => (
                  <tr key={i} className="hover:bg-slate-800/50 transition-colors">
                    {columns.map(col => <td key={col} className="py-2.5 px-3 text-slate-300">{row[col] != null ? (typeof row[col] === 'number' ? row[col].toLocaleString('en-IN', { maximumFractionDigits: 2 }) : String(row[col])) : '-'}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-center text-slate-500 py-10">No data found</p>}
        </div>
      </div>
    </div>
  );
};

// ─── FILTER BAR ───
const FilterBar = ({ filters, activeFilters, onFilterChange, onClearFilters, totalRows, unfilteredRows }) => {
  const categoryFilters = filters.filter(f => f.type === 'category');
  const dateFilters = filters.filter(f => f.type === 'date');
  const hasActiveFilter = activeFilters.filter_column || activeFilters.date_column;
  if (filters.length === 0) return null;
  return (
    <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-4 mb-6" data-testid="filter-bar">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-slate-400 text-sm font-medium"><Filter className="w-4 h-4 text-indigo-400" />Filters</div>
        {categoryFilters.map(f => (
          <Select key={f.column} value={activeFilters.filter_column === f.column ? activeFilters.filter_value : "all"} onValueChange={v => onFilterChange(f.column, v === "all" ? null : v, 'category')}>
            <SelectTrigger className="w-auto min-w-[140px] bg-slate-800 border-slate-600 text-slate-200 text-sm h-9" data-testid={`filter-${f.column}`}><SelectValue placeholder={f.column} /></SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-600">
              <SelectItem value="all" className="text-slate-300">All {f.column}</SelectItem>
              {f.values.map(v => <SelectItem key={v} value={v} className="text-slate-200">{v}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        {dateFilters.map(f => (
          <div key={f.column} className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <Select value={activeFilters.date_column === f.column ? (activeFilters.date_start || "all") : "all"} onValueChange={v => onFilterChange(f.column, v === "all" ? null : v, 'date')}>
              <SelectTrigger className="w-auto min-w-[160px] bg-slate-800 border-slate-600 text-slate-200 text-sm h-9" data-testid={`filter-date-${f.column}`}><SelectValue placeholder={f.column} /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="all" className="text-slate-300">All {f.column}</SelectItem>
                {f.values.map(v => <SelectItem key={v} value={v} className="text-slate-200">{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
        {hasActiveFilter && <Button variant="ghost" size="sm" onClick={onClearFilters} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 h-9 text-sm" data-testid="clear-filters"><X className="w-3.5 h-3.5 mr-1" /> Clear</Button>}
        {hasActiveFilter && <span className="text-xs text-slate-500 ml-auto">Showing {totalRows} of {unfilteredRows} rows</span>}
      </div>
    </div>
  );
};

// ─── MAIN PAGE ───
const ReportViewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [drilldown, setDrilldown] = useState({ open: false, column: null, value: null });
  const [mapTooltip, setMapTooltip] = useState({ show: false, x: 0, y: 0, content: null });

  const fetchDashboard = useCallback(async (isRefresh = false, filters = {}) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.filter_column) params.set('filter_column', filters.filter_column);
      if (filters.filter_value) params.set('filter_value', filters.filter_value);
      if (filters.date_column) params.set('date_column', filters.date_column);
      if (filters.date_start) params.set('date_start', filters.date_start);
      const qs = params.toString();
      const response = await axios.get(`${API_URL}/api/dashboards/${id}${qs ? '?' + qs : ''}`, { withCredentials: true });
      setDashboard(response.data);
    } catch (e) { console.error('Error fetching dashboard:', e); }
    finally { setLoading(false); setRefreshing(false); }
  }, [id]);

  useEffect(() => { fetchDashboard(false, activeFilters); }, [id]);

  const handleFilterChange = (column, value, type) => {
    let newFilters;
    if (type === 'date') newFilters = value ? { ...activeFilters, date_column: column, date_start: value } : { filter_column: activeFilters.filter_column, filter_value: activeFilters.filter_value };
    else newFilters = value ? { ...activeFilters, filter_column: column, filter_value: value } : { date_column: activeFilters.date_column, date_start: activeFilters.date_start };
    setActiveFilters(newFilters);
    fetchDashboard(false, newFilters);
  };
  const handleClearFilters = () => { setActiveFilters({}); fetchDashboard(false, {}); };

  const handleChartClick = (chartData, chart) => {
    if (!chartData?.activePayload) return;
    const payload = chartData.activePayload[0]?.payload;
    if (!payload) return;
    const col = chart.x_column;
    const val = payload.x || payload.name;
    if (col && val) setDrilldown({ open: true, column: col, value: String(val) });
  };
  const handlePieClick = (data, chart) => {
    if (data?.name && chart.x_column) setDrilldown({ open: true, column: chart.x_column, value: String(data.name) });
  };

  const renderChart = (chart, index) => {
    const chartData = chart.data || [];
    const colorIndex = index % GRADIENT_COLORS.length;
    const primaryColor = GRADIENT_COLORS[colorIndex][0];
    const secondaryColor = GRADIENT_COLORS[colorIndex][1];

    if (chart.chart_type === 'bar') {
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-indigo-400" />{chart.title}<span className="ml-auto text-xs text-slate-500 font-normal">Click bar to drill down</span></h3>
          <div className="h-72">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} onClick={(e) => handleChartClick(e, chart)}>
                  <defs><linearGradient id={`barG${index}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={primaryColor} /><stop offset="100%" stopColor={secondaryColor} stopOpacity={0.8} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="x" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} stroke="#475569" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="y" fill={`url(#barG${index})`} radius={[8, 8, 0, 0]} cursor="pointer" />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-slate-500">No data</div>}
          </div>
        </div>
      );
    }

    if (chart.chart_type === 'line') {
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-400" />{chart.title}</h3>
          <div className="h-72">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} onClick={(e) => handleChartClick(e, chart)}>
                  <defs><linearGradient id={`areaG${index}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={primaryColor} stopOpacity={0.4} /><stop offset="100%" stopColor={primaryColor} stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="x" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} stroke="#475569" />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="y" stroke={primaryColor} strokeWidth={3} fill={`url(#areaG${index})`} />
                  <Line type="monotone" dataKey="y" stroke={primaryColor} strokeWidth={3} dot={{ fill: primaryColor, r: 4 }} activeDot={{ r: 7, cursor: 'pointer' }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-slate-500">No data</div>}
          </div>
        </div>
      );
    }

    if (chart.chart_type === 'pie') {
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><PieChartIcon className="w-5 h-5 text-amber-400" />{chart.title}</h3>
          <div className="h-72">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ stroke: '#64748b' }}
                    onClick={(data) => handlePieClick(data, chart)} cursor="pointer">
                    {chartData.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} stroke="rgba(0,0,0,0.3)" strokeWidth={2} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ color: '#94a3b8' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-slate-500">No data</div>}
          </div>
        </div>
      );
    }

    if (chart.chart_type === 'stacked_bar') {
      const groupKeys = chart.group_keys || [];
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Layers className="w-5 h-5 text-violet-400" />{chart.title}</h3>
          <div className="h-72">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} onClick={(e) => handleChartClick(e, chart)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="x" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} stroke="#475569" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ color: '#94a3b8' }} />
                  {groupKeys.map((key, i) => <Bar key={key} dataKey={key} stackId="stack" fill={PIE_COLORS[i % PIE_COLORS.length]} radius={i === groupKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />)}
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-slate-500">No data</div>}
          </div>
        </div>
      );
    }

    if (chart.chart_type === 'combo') {
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><Activity className="w-5 h-5 text-teal-400" />{chart.title}</h3>
          <div className="h-72">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} onClick={(e) => handleChartClick(e, chart)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="x" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" angle={-45} textAnchor="end" height={60} />
                  <YAxis yAxisId="left" tick={{ fill: '#94a3b8', fontSize: 12 }} stroke="#475569" />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#94a3b8', fontSize: 12 }} stroke="#475569" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ color: '#94a3b8' }} />
                  <Bar yAxisId="left" dataKey="y" name={chart.y_label || 'Value 1'} fill={primaryColor} radius={[6, 6, 0, 0]} barSize={30} />
                  <Line yAxisId="right" type="monotone" dataKey="y2" name={chart.y2_label || 'Value 2'} stroke="#f59e0b" strokeWidth={3} dot={{ fill: '#f59e0b', r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-slate-500">No data</div>}
          </div>
        </div>
      );
    }

    if (chart.chart_type === 'waterfall') {
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl col-span-full" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><GitBranch className="w-5 h-5 text-rose-400" />{chart.title}</h3>
          <div className="h-80">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                  <XAxis dataKey="x" tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="#475569" angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} stroke="#475569" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="base" stackId="waterfall" fill="transparent" />
                  <Bar dataKey="y" stackId="waterfall" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, idx) => <Cell key={idx} fill={entry.isTotal ? '#6366f1' : entry.isPositive ? '#10b981' : '#ef4444'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="h-full flex items-center justify-center text-slate-500">No data</div>}
          </div>
        </div>
      );
    }

    if (chart.chart_type === 'treemap') {
      const TC = ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#f97316'];
      const TreemapContent = ({ x, y, width, height, name, value: val, index: idx }) => {
        if (width < 40 || height < 30) return null;
        return (<g><rect x={x} y={y} width={width} height={height} fill={TC[idx % TC.length]} stroke="#1e293b" strokeWidth={2} rx={4} style={{ opacity: 0.85 }} />
          <text x={x + width / 2} y={y + height / 2 - 8} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="600">{name}</text>
          <text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={11}>{typeof val === 'number' ? val.toLocaleString('en-IN') : val}</text></g>);
      };
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><PieChartIcon className="w-5 h-5 text-emerald-400" />{chart.title}</h3>
          <div className="h-72">
            {chartData.length > 0 ? <ResponsiveContainer width="100%" height="100%"><RechartsTreemap data={chartData} dataKey="value" nameKey="name" content={<TreemapContent />} /></ResponsiveContainer>
              : <div className="h-full flex items-center justify-center text-slate-500">No data</div>}
          </div>
        </div>
      );
    }

    if (chart.chart_type === 'gauge') {
      const gd = chart.data || {};
      const pct = chart.percentage || 0;
      const gaugeColor = pct >= 100 ? '#10b981' : pct >= 70 ? '#f59e0b' : '#ef4444';
      const angle = Math.min(pct, 150) * 1.8;
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-amber-400" />{chart.title}</h3>
          <div className="flex flex-col items-center justify-center h-60">
            <div className="relative w-48 h-28 overflow-hidden">
              <svg viewBox="0 0 200 110" className="w-full h-full">
                <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#334155" strokeWidth="16" strokeLinecap="round" />
                <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke={gaugeColor} strokeWidth="16" strokeLinecap="round" strokeDasharray={`${angle * 0.87} 999`} className="transition-all duration-1000" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
                <span className="text-3xl font-black text-white">{formatValue(gd.actual)}</span>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4 text-sm">
              <span className="text-slate-400">Target: <span className="text-white font-semibold">{formatValue(gd.target)}</span></span>
              <span className={`font-bold px-2 py-0.5 rounded-full text-xs ${pct >= 100 ? 'bg-emerald-500/20 text-emerald-400' : pct >= 70 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>{pct}%</span>
            </div>
          </div>
        </div>
      );
    }

    if (chart.chart_type === 'map') {
      const mapData = chart.data || [];
      const maxVal = chart.max_value || 1;
      const dataMap = {};
      mapData.forEach(d => { dataMap[d.iso3] = d; });

      // Color scale: slate -> deep indigo
      const getColor = (iso3) => {
        const entry = dataMap[iso3];
        if (!entry) return '#1e293b';
        const ratio = entry.value / maxVal;
        if (ratio > 0.7) return '#4f46e5';
        if (ratio > 0.4) return '#6366f1';
        if (ratio > 0.2) return '#818cf8';
        if (ratio > 0.05) return '#a5b4fc';
        return '#c7d2fe';
      };

      const getIso3FromGeo = (geo) => {
        return ISO_NUMERIC_TO_ALPHA3[String(geo.id)] || geo.properties?.ISO_A3 || '';
      };

      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl col-span-full" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-cyan-400" />{chart.title}
          </h3>
          {mapData.length > 0 ? (
            <div className="relative">
              <div className="h-[420px]">
                <ComposableMap
                  projectionConfig={{ rotate: [-10, 0, 0], scale: 147 }}
                  style={{ width: '100%', height: '100%' }}
                >
                  <ZoomableGroup center={[0, 20]} zoom={1}>
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const iso3 = getIso3FromGeo(geo);
                          const entry = dataMap[iso3];
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={getColor(iso3)}
                              stroke="#334155"
                              strokeWidth={0.5}
                              style={{
                                default: { outline: 'none' },
                                hover: { fill: entry ? '#f59e0b' : '#334155', outline: 'none', cursor: entry ? 'pointer' : 'default' },
                                pressed: { outline: 'none' },
                              }}
                              onMouseEnter={(evt) => {
                                if (entry) {
                                  setMapTooltip({ show: true, x: evt.clientX, y: evt.clientY, content: entry });
                                }
                              }}
                              onMouseMove={(evt) => {
                                if (entry) {
                                  setMapTooltip(prev => ({ ...prev, x: evt.clientX, y: evt.clientY }));
                                }
                              }}
                              onMouseLeave={() => setMapTooltip({ show: false, x: 0, y: 0, content: null })}
                              onClick={() => {
                                if (entry && chart.x_column) {
                                  setDrilldown({ open: true, column: chart.x_column, value: entry.name });
                                }
                              }}
                            />
                          );
                        })
                      }
                    </Geographies>
                  </ZoomableGroup>
                </ComposableMap>
              </div>
              {/* Map Tooltip */}
              {mapTooltip.show && mapTooltip.content && (
                <div
                  className="fixed z-50 pointer-events-none bg-slate-900/95 backdrop-blur-sm border border-slate-700 rounded-xl px-4 py-3 shadow-2xl"
                  style={{ left: mapTooltip.x + 12, top: mapTooltip.y - 60 }}
                  data-testid="map-tooltip"
                >
                  <p className="text-white font-semibold text-sm">{mapTooltip.content.name}</p>
                  <p className="text-indigo-300 text-sm font-bold">{formatValue(mapTooltip.content.value)}</p>
                  <p className="text-slate-400 text-xs">{mapTooltip.content.pct}% of top</p>
                </div>
              )}
              {/* Legend + Top countries */}
              <div className="mt-4 flex flex-wrap gap-6 items-start">
                {/* Color legend */}
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>Low</span>
                  <div className="flex h-3 rounded-full overflow-hidden">
                    {['#c7d2fe','#a5b4fc','#818cf8','#6366f1','#4f46e5'].map(c => (
                      <div key={c} className="w-6 h-full" style={{ backgroundColor: c }}></div>
                    ))}
                  </div>
                  <span>High</span>
                </div>
                {/* Top countries list */}
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {mapData.slice(0, 8).map((entry, i) => (
                    <div key={entry.iso3} className="flex items-center gap-2 bg-slate-800/50 rounded-lg px-3 py-2" data-testid={`map-country-${i}`}>
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getColor(entry.iso3) }}></div>
                      <span className="text-xs text-slate-300 truncate">{entry.name}</span>
                      <span className="text-xs text-white font-semibold ml-auto">{formatValue(entry.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : <div className="h-80 flex items-center justify-center text-slate-500">No geographical data available</div>}
        </div>
      );
    }

    if (chart.chart_type === 'table') {
      const tData = chart.data || [];
      const cols = chart.columns || [];
      return (
        <div key={index} className="bg-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl col-span-full" data-testid={`chart-${index}`}>
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2"><FileText className="w-5 h-5 text-cyan-400" />{chart.title}</h3>
          <div className="overflow-x-auto max-h-96 rounded-xl border border-slate-700/50">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800/90 backdrop-blur-sm"><tr>{cols.map(c => <th key={c} className="text-left py-4 px-4 text-xs font-semibold tracking-wider uppercase text-indigo-300 border-b border-slate-700">{c}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-700/50">
                {tData.slice(0, 50).map((row, ri) => <tr key={ri} className="hover:bg-slate-800/50 transition-colors">{cols.map(c => <td key={c} className="py-3 px-4 text-slate-300">{row[c] != null ? (typeof row[c] === 'number' ? row[c].toLocaleString('en-IN', { maximumFractionDigits: 2 }) : String(row[c])) : '-'}</td>)}</tr>)}
              </tbody>
            </table>
            {chart.total_rows > 50 && <p className="text-center text-sm text-slate-500 py-3 bg-slate-800/50">Showing 50 of {chart.total_rows.toLocaleString()} rows</p>}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 animate-pulse">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 p-6">
        <div className="bg-slate-900/50 border border-slate-700/50 rounded-2xl p-12 text-center">
          <p className="text-slate-400">Dashboard not found</p>
          <Button onClick={() => navigate('/reports')} className="mt-4 bg-indigo-600 hover:bg-indigo-700">Back to Dashboards</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950" data-testid="report-view-page">
      {/* Animated BG */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/reports')} className="text-slate-400 hover:text-white hover:bg-slate-800/50"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white via-indigo-200 to-purple-200 bg-clip-text text-transparent">{dashboard.name}</h1>
              {dashboard.description && <p className="text-slate-400 mt-1 max-w-2xl">{dashboard.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-800/50 px-4 py-2 rounded-full"><FileText className="w-4 h-4 text-indigo-400" />{dashboard.file_name}</div>
            <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-800/50 px-4 py-2 rounded-full"><Clock className="w-4 h-4 text-emerald-400" />{dashboard.last_data_update ? format(new Date(dashboard.last_data_update), 'MMM d, HH:mm') : '-'}</div>
            <Button variant="outline" size="sm" onClick={() => fetchDashboard(true, activeFilters)} disabled={refreshing} className="border-slate-600 text-slate-300 hover:bg-slate-800"><RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh</Button>
          </div>
        </div>

        {/* 1 - ALERTS */}
        <AlertsSection alerts={dashboard.alerts} />

        {/* Filters */}
        <FilterBar filters={dashboard.filterable_columns || []} activeFilters={dashboard.active_filters || {}} onFilterChange={handleFilterChange} onClearFilters={handleClearFilters} totalRows={dashboard.total_rows} unfilteredRows={dashboard.unfiltered_rows} />

        {/* 5 - VITAL SIGNS (KPI Cards) */}
        {dashboard.computed_kpis?.length > 0 && (
          <div className={`grid gap-5 mb-8 ${dashboard.computed_kpis.length <= 3 ? 'grid-cols-1 md:grid-cols-3' : dashboard.computed_kpis.length <= 5 ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'}`}>
            {dashboard.computed_kpis.map((kpi, i) => (
              <KPICard key={i} title={kpi.title} value={kpi.value} format={kpi.format} index={i} health={kpi.health || 'normal'} audit={kpi.audit} />
            ))}
          </div>
        )}

        {/* 3 - CORE CHARTS */}
        {dashboard.computed_charts?.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dashboard.computed_charts.map((chart, i) => renderChart(chart, i))}
          </div>
        )}

        {(!dashboard.computed_kpis?.length && !dashboard.computed_charts?.length) && (
          <div className="bg-slate-900/50 border border-slate-700/50 rounded-2xl p-12 text-center">
            <p className="text-slate-400">No visualizations configured for this dashboard.</p>
          </div>
        )}
      </div>

      <DrilldownModal open={drilldown.open} onClose={() => setDrilldown({ open: false, column: null, value: null })} dashboardId={id} column={drilldown.column} value={drilldown.value} />
    </div>
  );
};

export default ReportViewPage;
